import { useRef } from 'react';
import { arrangements } from '@/mocks/arrangements';
import { useBag } from '@/components/feature/bag/BagContext';
import useEditorialMotion from '@/hooks/useEditorialMotion';

type Item = (typeof arrangements)[number];

function Piece({ item, ratio, dir }: { item: Item; ratio: string; dir: 'left' | 'right' }) {
  const { openProduct } = useBag();
  return (
    <button
      type="button"
      onClick={() => openProduct(item.id)}
      data-m-in={dir}
      data-m-dist="55"
      data-m-at={dir === 'right' ? 0.12 : 0}
      className="group text-left flex flex-col w-full min-w-0 cursor-pointer"
    >
      <div className={`w-full ${ratio} overflow-hidden`}>
        <div className="w-full h-full" data-m-par={dir === 'left' ? 28 : 52} data-m-par-t={dir === 'left' ? 10 : 20} data-m-cover>
          <img src={item.image} width={1000} height={1250} loading="lazy" decoding="async" alt={`${item.name} floral arrangement — Fleur & Form`} title={`${item.name} — Fleur & Form`} className="w-full h-full object-cover object-center transition-transform duration-[420ms] ease-out group-hover:scale-[1.025]" />
        </div>
      </div>
      <div className="flex items-baseline justify-between gap-3 pt-3 md:pt-4">
        <div className="flex items-baseline gap-3 min-w-0">
          <span className="text-[11px] text-black/40">{item.no}</span>
          <span className="text-[16px] md:text-[18px] tracking-[-0.02em] truncate">{item.name}</span>
        </div>
        <span className="flex items-baseline gap-2 text-[13px] whitespace-nowrap">
          ${item.price}
          <span className="text-black/40 transition-transform duration-[240ms] group-hover:translate-x-1 group-hover:text-black">→</span>
        </span>
      </div>
    </button>
  );
}

export default function Arrangements() {
  const ref = useRef<HTMLElement>(null);
  useEditorialMotion(ref);
  return (
    <section ref={ref} id="arrangements" className="bg-white pt-20 md:pt-40 pb-20 md:pb-36">
      <div className="px-4 md:px-10 mb-10 md:mb-16">
        <p data-m-fade className="text-[11px] md:text-[12px] tracking-wide text-black/50 mb-4">04 / FLOWER STUDIES</p>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 data-m-head className="font-normal tracking-[-0.04em] leading-none text-[40px] md:text-[72px]">Selected Arrangements</h2>
          <p data-m-fade data-m-at="0.15" className="ff-serif italic text-[16px] md:text-[18px] text-black/60">Made with the season.</p>
        </div>
      </div>
      <div className="px-4 md:px-10 flex flex-col gap-12 md:gap-24 lg:gap-32">
        <div className="grid grid-cols-1 md:grid-cols-[60fr_40fr] gap-12 md:gap-10 lg:gap-16 items-start">
          <Piece item={arrangements[0]} ratio="aspect-[4/5]" dir="left" />
          <div className="md:mt-16">
            <Piece item={arrangements[1]} ratio="aspect-[4/5]" dir="right" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[42fr_58fr] gap-12 md:gap-10 lg:gap-16 items-end">
          <div className="md:mb-24 order-1">
            <Piece item={arrangements[2]} ratio="aspect-[3/4]" dir="left" />
          </div>
          <div className="order-2">
            <Piece item={arrangements[3]} ratio="aspect-[5/6]" dir="right" />
          </div>
        </div>
      </div>
    </section>
  );
}